/*
 * The Diplomacy screen: every other side, and where this commander stands
 * with them (allied, offered, being offered, or neither, with the one thing
 * to do about it); then agreements: any number of sides under one name,
 * their grants their terms (peace, shared sight, pooled findings), with
 * proposals waiting and a form to propose one.
 */

import { For, Show, createSignal, type JSX } from "solid-js";
import { diplomacy, diplomacyTick, hasFeeds, me, type Feed, type Grants } from "../state/diplomacy";

/** A feed's terms in words ("15 min old, our ground only"). */
const feedTerms = (f: Feed, ours: boolean) => {
  const left = typeof f.until === "number" ? Math.max(0, (f.until - diplomacyTick()) / 600) : null;
  // Whole minutes first, so 119.9 reads "2 h", not "1 h 60 min".
  const m = left === null ? 0 : Math.ceil(left);
  const time = left === null ? "" : m >= 60 ? `, ${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""} left` : `, ${m} min left`;
  const unpaid = typeof f.unpaid_until === "number" ? `, unpaid: ends in ${Math.max(0, Math.ceil((f.unpaid_until - diplomacyTick()) / 600))} min` : "";
  // Not to be passed on: theirs to keep (said on either side).
  const keep = f.reshare === false ? (ours ? ", not to pass on" : ", theirs only") : "";
  return `${f.delay ? `${f.delay} min old` : "as soon as seen"}${f.own_ground ? `, ${ours ? "our" : "their"} ground only` : ""}${keep}${time}${unpaid}`;
};
const DELAYS = [0, 5, 15, 60];

/** Sharing what we see with one side (an intel feed): the terms, and
 *  share, change or stop. */
function FeedControl(p: { side: number; send: (c: Record<string, unknown>) => void }): JSX.Element {
  const given = () => diplomacy().feedsGiven.find((f) => f.side === p.side);
  const [delay, setDelay] = createSignal(given()?.delay ?? 15);
  const [own, setOwn] = createSignal(given()?.own_ground ?? false);
  const [reshare, setReshare] = createSignal(given()?.reshare ?? true);
  const changed = () => !given() || given()!.delay !== delay() || given()!.own_ground !== own() || (given()!.reshare ?? true) !== reshare();
  return (
    <div class="feed-control">
      <span class="row-label" data-tip="They see what you've seen (your last sightings, not live sight), once it's this old">
        Share what we see
      </span>
      <select value={String(delay())} onChange={(e) => setDelay(Number(e.currentTarget.value))} aria-label="How old">
        <For each={DELAYS}>{(d) => <option value={String(d)}>{d ? `${d} min old` : "At once"}</option>}</For>
      </select>
      <label class="plan-option" data-tip="Only what you've seen on your own claimed ground">
        <input type="checkbox" checked={own()} onChange={(e) => setOwn(e.currentTarget.checked)} />
        Our ground only
      </label>
      <label class="plan-option" data-tip="Whether they may include what you share in feeds of their own to others">
        <input type="checkbox" checked={reshare()} onChange={(e) => setReshare(e.currentTarget.checked)} />
        Let them pass it on
      </label>
      <Show when={changed()}>
        <button onClick={() => p.send({ type: "feed", to: p.side, delay: delay(), own_ground: own(), reshare: reshare() })}>{given() ? "Change" : "Share"}</button>
      </Show>
      <Show when={given()}>
        <button class="ghost" onClick={() => p.send({ type: "unfeed", to: p.side })}>
          Stop sharing
        </button>
      </Show>
    </div>
  );
}
import { sideAvatar, sideName } from "../state/players";

// The proposal being drawn up (kept while the screen's closed).
const [name, setName] = createSignal("");
const [picked, setPicked] = createSignal<number[]>([]);
const [grants, setGrants] = createSignal<Grants>({ peace: true, sight: false, findings: false, sightings: false });
// Sides the members would promise not to fire on.
const [forbid, setForbid] = createSignal<number[]>([]);
// Inviting into an agreement: which one, and whom.
const [inviting, setInviting] = createSignal<number | null>(null);

const GRANT_WORDS: [keyof Grants, string, string][] = [
  ["peace", "Peace", "No firing on each other, no taking each other's depots"],
  ["sight", "Shared sight", "Each sees what the others' units see"],
  ["findings", "Pooled findings", "Prospecting results shared every 30 s"],
  ["sightings", "Pooled sightings", "Each remembers what the others last saw: not live sight, but the memory of it"],
];

export function DiplomacyScreen(p: { sides: { name: string; color: string }[]; send: (c: Record<string, unknown>) => void }): JSX.Element {
  // Not the storm's creatures (v31.13): no one treats with them.
  const others = () => p.sides.map((s, i) => ({ ...s, side: i })).filter((s) => s.side !== me() && s.name !== "Storm");
  const stand = (side: number) => {
    const d = diplomacy();
    return d.allies.includes(side) ? "allied" : d.offers.includes(side) ? "offers" : d.offered.includes(side) ? "offered" : "none";
  };
  const WORDS: Record<string, string> = {
    allied: "Allied: you hold fire on each other, can't take each other's depots, and their armed units guard yours.",
    offers: "They're offering you an alliance.",
    offered: "You've offered them an alliance; it starts when they accept.",
    none: "Not allied: your units fire on theirs.",
  };
  const face = (side: number, size = 22) => (
    <Show when={sideAvatar(side)} fallback={<span class="swatch" style={{ background: p.sides[side]?.color ?? "#888" }} />}>
      {(src) => <img class="avatar" src={src()} alt="" width={size} height={size} style={{ "border-color": p.sides[side]?.color ?? "#888" }} title={sideName(side)} />}
    </Show>
  );
  const names = (sides: number[]) => sides.map((x) => (x === me() ? "you" : sideName(x))).join(", ");
  const chips = (g: Grants, forbid?: number[]) => (
    <span class="grant-chips">
      <For each={GRANT_WORDS.filter(([k]) => g[k])}>
        {([, label, tip]) => (
          <span class="grant-chip" data-tip={tip}>
            {label}
          </span>
        )}
      </For>
      <Show when={forbid?.length}>
        <span class="grant-chip" data-tip="Members promise not to fire on them; one who does can be expelled by the others">
          Not firing on {names(forbid!)}
        </span>
      </Show>
    </span>
  );
  // Expelling a member in breach: asked first, saying if it ends peace.
  const expel = (a: { id: number; name: string; grants: Grants }, who: number) => {
    const peace = a.grants.peace ? " You'll no longer be at peace with them." : "";
    if (confirm(`Expel ${sideName(who)} from "${a.name}"?${peace}`)) p.send({ type: "expel", agreement: a.id, who });
  };
  // Ending peace: the one thing asked first (it can't be taken back
  // without the others agreeing again).
  const breakAlliance = (side: number) => {
    if (confirm(`Break your alliance with ${sideName(side)}? Your units will fire on each other again.`)) p.send({ type: "unally", with: side });
  };
  const leave = (a: { id: number; name: string; members: number[]; grants: Grants }) => {
    const rest = a.members.filter((m) => m !== me());
    const peace = a.grants.peace && rest.length ? ` You'd no longer be at peace with ${names(rest)}.` : "";
    if (!peace || confirm(`Leave "${a.name}"?${peace}`)) p.send({ type: "leave", agreement: a.id });
  };
  const propose = () => {
    if (!picked().length) return;
    p.send({ type: "propose", name: name().trim() || "Agreement", with: picked(), grants: grants(), forbid: forbid().filter((x) => !picked().includes(x)) });
    setName("");
    setPicked([]);
    setForbid([]);
  };
  return (
    <section class="depth-screen diplomacy" aria-label="Diplomacy">
      <h2>Diplomacy</h2>
      <Show when={others().length} fallback={<p class="muted">No one else is here yet.</p>}>
        <div class="diplomacy-list">
          <For each={others()}>
            {(s) => (
              <div class="diplomacy-row" classList={{ allied: stand(s.side) === "allied", offers: stand(s.side) === "offers" }}>
                {/* Their commander's avatar, ringed in their colour (or the colour). */}
                {face(s.side, 28)}
                <span class="name">{sideName(s.side)}</span>
                <span class="muted">{WORDS[stand(s.side)]}</span>
                <span class="actions">
                  <Show when={stand(s.side) === "none"}>
                    <button onClick={() => p.send({ type: "ally", with: s.side })}>Offer alliance</button>
                  </Show>
                  <Show when={stand(s.side) === "offers"}>
                    <button class="primary" onClick={() => p.send({ type: "ally", with: s.side })}>
                      Accept
                    </button>
                    <button class="ghost" onClick={() => p.send({ type: "unally", with: s.side })}>
                      Decline
                    </button>
                  </Show>
                  <Show when={stand(s.side) === "offered"}>
                    <button class="ghost" onClick={() => p.send({ type: "unally", with: s.side })}>
                      Withdraw offer
                    </button>
                  </Show>
                  <Show when={stand(s.side) === "allied"}>
                    <button onClick={() => breakAlliance(s.side)}>Break alliance</button>
                  </Show>
                </span>
                <Show when={hasFeeds()}>
                  <FeedControl side={s.side} send={p.send} />
                </Show>
              </div>
            )}
          </For>
        </div>

        <Show when={hasFeeds()}>
          <h2 class="diplomacy-section">Intel feeds</h2>
          <Show when={diplomacy().feedsGiven.length || diplomacy().feedsGot.length} fallback={<p class="muted">You share sightings with no one, and no one with you.</p>}>
            <ul class="feed-list">
              <For each={diplomacy().feedsGiven}>
                {(f) => (
                  <li>
                    You give {sideName(f.side)} what you see: {feedTerms(f, true)}
                  </li>
                )}
              </For>
              <For each={diplomacy().feedsGot}>
                {(f) => (
                  <li>
                    {sideName(f.side)} gives you what they see: {feedTerms(f, false)}
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>

        <h2 class="diplomacy-section">Agreements</h2>
        <Show when={diplomacy().agreements.length} fallback={<p class="muted">You're in no agreements.</p>}>
          <div class="diplomacy-list">
            <For each={diplomacy().agreements}>
              {(a) => (
                <div class="agreement-row">
                  <span class="name">{a.name}</span>
                  <span class="faces">
                    <For each={a.members}>{(m) => face(m)}</For>
                  </span>
                  {chips(a.grants, a.forbid)}
                  <span class="actions">
                    <Show
                      when={inviting() === a.id}
                      fallback={
                        <Show when={others().some((o) => !a.members.includes(o.side))}>
                          <button onClick={() => setInviting(a.id)}>Invite</button>
                        </Show>
                      }
                    >
                      <select
                        onChange={(e) => {
                          const who = Number(e.currentTarget.value);
                          if (who >= 0) p.send({ type: "invite", agreement: a.id, who });
                          setInviting(null);
                        }}
                      >
                        <option value="-1">Invite whom…</option>
                        <For each={others().filter((o) => !a.members.includes(o.side))}>{(o) => <option value={o.side}>{sideName(o.side)}</option>}</For>
                      </select>
                    </Show>
                    <button class="ghost" onClick={() => leave(a)}>
                      Leave
                    </button>
                  </span>
                  {/* Breaches: who fired on a side they promised not to. */}
                  <Show when={a.breaches?.length}>
                    <div class="breaches">
                      <For each={[...new Set(a.breaches!.map((b) => b.member))]}>
                        {(m) => (
                          <div class="breach">
                            <span class="warn">
                              {m === me() ? "You" : sideName(m)} fired on{" "}
                              {names([...new Set(a.breaches!.filter((b) => b.member === m).map((b) => b.fired_on))])}, breaking the promise.
                              {m === me() ? " The others may expel you." : ""}
                            </span>
                            <Show when={m !== me()}>
                              <button onClick={() => expel(a, m)}>Expel</button>
                            </Show>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>

        <Show when={diplomacy().proposals.length}>
          <h3>Waiting</h3>
          <div class="diplomacy-list">
            <For each={diplomacy().proposals}>
              {(pr) => {
                const mine = () => pr.by === me();
                const asksMe = () => pr.asked.includes(me()) && !pr.accepted.includes(me());
                const into = () => (pr.joins !== null ? diplomacy().agreements.find((a) => a.id === pr.joins)?.name : null);
                return (
                  <div class="agreement-row" classList={{ offers: asksMe() }}>
                    <span class="name">
                      {into() ? `Invitation into ${into()}` : pr.name}
                      <span class="muted small"> · from {mine() ? "you" : sideName(pr.by)}</span>
                    </span>
                    <span class="faces">
                      <For each={pr.members}>{(m) => face(m)}</For>
                    </span>
                    {chips(pr.grants, pr.forbid)}
                    <span class="muted small">
                      {pr.accepted.length ? `Accepted: ${names(pr.accepted)}` : "No one's accepted yet"}
                      {pr.asked.filter((x) => !pr.accepted.includes(x)).length ? ` · waiting for ${names(pr.asked.filter((x) => !pr.accepted.includes(x)))}` : ""}
                    </span>
                    <span class="actions">
                      <Show when={asksMe()}>
                        <button class="primary" onClick={() => p.send({ type: "join", proposal: pr.id })}>
                          Accept
                        </button>
                      </Show>
                      <button class="ghost" onClick={() => p.send({ type: "decline", proposal: pr.id })}>
                        {mine() ? "Withdraw" : "Decline"}
                      </button>
                    </span>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>

        <h3>Propose an agreement</h3>
        <div class="propose-form">
          <span class="row-label">Name</span>
          <input type="text" maxLength={40} placeholder="e.g. Northern pact" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          <span class="row-label">With</span>
          <span class="propose-picks">
            <For each={others()}>
              {(o) => (
                <label>
                  <input
                    type="checkbox"
                    checked={picked().includes(o.side)}
                    onChange={(e) => setPicked(e.currentTarget.checked ? [...picked(), o.side] : picked().filter((x) => x !== o.side))}
                  />
                  {face(o.side, 18)} {sideName(o.side)}
                </label>
              )}
            </For>
          </span>
          <span class="row-label">Terms</span>
          <span class="propose-picks">
            <For each={GRANT_WORDS}>
              {([k, label, tip]) => (
                <label data-tip={tip}>
                  <input type="checkbox" checked={grants()[k]} onChange={(e) => setGrants({ ...grants(), [k]: e.currentTarget.checked })} />
                  {label}
                </label>
              )}
            </For>
          </span>
          <span class="row-label">Promise not to fire on</span>
          <span class="propose-picks">
            <For each={others().filter((o) => !picked().includes(o.side))} fallback={<span class="muted small">No one else to name.</span>}>
              {(o) => (
                <label data-tip="Members promise not to fire on them; one who does can be expelled by the others">
                  <input
                    type="checkbox"
                    checked={forbid().includes(o.side)}
                    onChange={(e) => setForbid(e.currentTarget.checked ? [...forbid(), o.side] : forbid().filter((x) => x !== o.side))}
                  />
                  {face(o.side, 18)} {sideName(o.side)}
                </label>
              )}
            </For>
          </span>
          <span />
          <span>
            <button class="primary" disabled={!picked().length} onClick={propose}>
              Propose
            </button>
            <span class="muted small"> It stands once everyone asked accepts.</span>
          </span>
        </div>
      </Show>
    </section>
  );
}
